import SagaDuck from '@duckness/saga'
import type { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'
import { duckName } from './constants'
import { EXPORT_ACTIONS, type ExportDuckActionType } from './ExportDuck.actions'
import type { ComponentStoreState } from '@aytm/app/insights/dashboard/stores/ComponentStoreState'
import type { ExportInitiatePayloadType, ExportStateType } from '../../../stores/ExportStateType'
import type { SubjectType } from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import type { ServerResponse } from '@aytm/app/insights/dashboard/models/types.ServerResponse'
// import type { ExportDuckSelectorsType } from './ExportDuck.types'

export type ExportDuckSelectorsType = {
  isExporting: (state: InsightsDashboardPoolState) => boolean
  exportProgress: (state: InsightsDashboardPoolState) => number
  availableSubjects: (state: InsightsDashboardPoolState) => ComponentStoreState['subjects']
  fileFormat: (state: InsightsDashboardPoolState) => ExportStateType['dialog']['type']
  dialog: (state: InsightsDashboardPoolState) => ExportStateType['dialog']
  error: (state: InsightsDashboardPoolState) => ExportStateType['error']
  lastResultText: (state: InsightsDashboardPoolState) => ExportStateType['lastResultText']
  lastResultData: (state: InsightsDashboardPoolState) => ExportStateType['lastResultData']
}

export const ExportDuck = SagaDuck<InsightsDashboardPoolState, ExportDuckActionType, ExportDuckSelectorsType>(
  duckName,
  'insights-dashboard'
)

// Register actions
ExportDuck.action(EXPORT_ACTIONS.EXPORT_START)
ExportDuck.action(EXPORT_ACTIONS.EXPORT_REQUEST)
ExportDuck.action(EXPORT_ACTIONS.EXPORT_PROGRESS)
ExportDuck.action(EXPORT_ACTIONS.EXPORT_SUCCESS)
ExportDuck.action(EXPORT_ACTIONS.EXPORT_SUCCESS_CLEANUP)
ExportDuck.action(EXPORT_ACTIONS.EXPORT_ERROR)
ExportDuck.action(EXPORT_ACTIONS.EXPORT_CANCEL)
ExportDuck.action(EXPORT_ACTIONS.EXPORT_UPDATE_DIALOG)

// Register selectors (just before export default)
ExportDuck.selector('isExporting', state => state.exportState?.isExporting || false)
ExportDuck.selector('exportProgress', state => state.exportState?.progress || 0)
ExportDuck.selector('availableSubjects', state => state.currentData?.subjects || ({} as Record<string, SubjectType>))
ExportDuck.selector('fileFormat', state => state.exportState?.dialog?.type || null)
ExportDuck.selector('dialog', state => state.exportState?.dialog || ({} as ExportStateType['dialog']))
ExportDuck.selector('error', state => state.exportState?.error || null)
ExportDuck.selector('lastResultText', state => state.exportState?.lastResultText || null)
ExportDuck.selector('lastResultData', state => state.exportState?.lastResultData || null)

// Reducers
ExportDuck.reducer(
  '@@INIT',
  state =>
    ({
      ...state,
      exportState: {
        isExporting: false,
        initiatedAt: 0,
        progress: 0,
        dialog: {
          selectedItemIds: [],
          options: [],
          type: 'pptx'
        },
        error: null,
        lastResultText: null
      }
    }) as InsightsDashboardPoolState
)

ExportDuck.reducer<{
  params: ExportInitiatePayloadType
  timestamp: number
}>(
  EXPORT_ACTIONS.EXPORT_START,
  (state, action) =>
    ({
      ...state,
      exportState: {
        ...(state.exportState || {}),
        isExporting: true,
        initiatedAt: action?.payload.timestamp || 0,
        progress: 0,
        error: null,
        lastResultText: null,
        lastResultData: null
      }
    }) as InsightsDashboardPoolState
)

ExportDuck.reducer<ServerResponse.InsightsExport.PositiveServerResponse['exportResult']>(
  EXPORT_ACTIONS.EXPORT_SUCCESS,
  (state, action) =>
    ({
      ...state,
      exportState: {
        ...(state.exportState || {}),
        isExporting: false,
        initiatedAt: 0,
        progress: 100,
        lastResultText: 'success',
        lastResultData: action.payload,
        error: null
      }
    }) as InsightsDashboardPoolState
)

ExportDuck.reducer<{ progress: number }>(
  EXPORT_ACTIONS.EXPORT_PROGRESS,
  (state, action) =>
    ({
      ...state,
      exportState: {
        ...(state.exportState || {}),
        isExporting: true,
        progress: action.payload.progress || 0,
        error: null,
        lastResultText: null
      }
    }) as InsightsDashboardPoolState
)

ExportDuck.reducer(
  EXPORT_ACTIONS.EXPORT_ERROR,
  state =>
    ({
      ...state,
      exportState: {
        ...(state.exportState || {}),
        isExporting: false,
        initiatedAt: 0,
        progress: 0,
        lastResultText: 'error',
        lastResultData: null
      }
    }) as InsightsDashboardPoolState
)

ExportDuck.reducer(
  EXPORT_ACTIONS.EXPORT_CANCEL,
  state =>
    ({
      ...state,
      exportState: {
        ...(state.exportState || {}),
        isExporting: false,
        initiatedAt: 0,
        progress: 0,
        lastResultData: null,
        lastResultText: 'canceled',
        error: null
      }
    }) as InsightsDashboardPoolState
)

ExportDuck.reducer(
  EXPORT_ACTIONS.EXPORT_SUCCESS_CLEANUP,
  state =>
    ({
      ...state,
      exportState: {
        ...(state.exportState || {}),
        isExporting: false,
        initiatedAt: 0,
        progress: 0,
        // lastResultText: null,
        lastResultData: null,
        error: null
      }
    }) as InsightsDashboardPoolState
)

ExportDuck.reducer<Partial<ExportStateType['dialog']>>(
  EXPORT_ACTIONS.EXPORT_UPDATE_DIALOG,
  (state, action) =>
    ({
      ...state,
      exportState: {
        ...(state.exportState || {}),
        dialog: {
          ...(state.exportState?.dialog || {}),
          ...action.payload
        }
      }
    }) as InsightsDashboardPoolState
)
